export const BREAKTHROUGH_GUARDRAILS = `
Guardrails (apply to every section of this report):
- Treat all interpretations as coaching hypotheses to explore, not facts about the person.
- Do not diagnose, pathologise, or use alarming language such as "alarmingly high," "dangerously low," or similar clinical-sounding phrasing.
- Never infer intelligence, career suitability, relationship behaviour, or the frequency of emotional episodes unless they are directly measured by this assessment.
- Do not repeat the same insight across multiple sections; each section must add new information.
- Include specific questions the coach can ask the respondent to validate each major interpretation.
- Clearly separate client-facing insights (written for the respondent) from private coach notes (written for the practitioner). Use a dedicated "Coach Notes" section for the latter.
- When exploring likely beliefs, negative emotions, or internal/parts conflict, frame them as possibilities to investigate in session, not confirmed realities.
- Do not include generation instructions or meta-commentary such as "Continue generating" in the report output.
`;

export const BREAKTHROUGH_REPORT_PROMPT = `Generate a comprehensive NLP OS™ Breakthrough Report for this respondent.

This is NOT a general personality summary. This is a focused coaching report that identifies the highest-leverage areas for breakthrough and change.

Use the 5-step evaluation method:
1. Identify the strongest blocker (the highest score among Antagonism and below on the Harmonic Scale). This is the emotional state most likely to be interfering with the person's potential.
2. Identify the lowest characteristic. This is the trait most in need of development.
3. Identify the highest characteristic. This is the strongest available resource.
4. Explain how the strongest blocker may suppress, distort, or override both the highest and lowest traits — and why the person may not be fully living their potential.
5. Create a strategy for shifting the blocker and unlocking the person's potential.

Classify the findings into three zones:
- Critical Breakthrough Zone: The area most likely holding back progress (usually driven by the highest blocker interacting with the lowest trait).
- Development Breakthrough Zone: The trait or behaviour requiring focused growth.
- Leverage Zone: The strongest existing resource (highest trait and/or highest resource state) that can support the change.

For each zone, explain WHY it was selected — not just the score. Show the interaction between the blocker and the traits.

The report must include:

1. Breakthrough Overview — 600–800 words
   Summarise the main breakthrough opportunity in coaching language. Name the primary blocker, the lowest trait, and the highest trait. Explain the core story of why this person may be stuck and where the doorway to change is.

2. Critical Breakthrough Zone — 1,000–1,300 words
   Deep analysis of the highest blocker state. How it may show up in the person's life, work, and relationships. How it may suppress or distort the strongest traits. What shifting it could unlock. Include specific questions the coach can ask to validate this interpretation.

3. Development Breakthrough Zone — 800–1,100 words
   Deep analysis of the lowest characteristic. Why it matters. How the blocker may be interfering with its development. What growth in this area could make possible. Include practical development suggestions.

4. Leverage Zone — 700–1,000 words
   How the highest characteristic and highest resource state can be used as a resource for change. How to amplify them. How to prevent the blocker from draining or distorting them. Include practical ways to leverage each strength.

5. Blocker–Trait Interaction — 800–1,100 words
   Detailed analysis of how the highest blocker interacts with both the highest and lowest traits. Explain the override pattern: how a high blocker can suppress a high trait, distort a medium trait, or deepen a low trait. Use examples from professional, personal, and relational contexts.

6. Likely Beliefs & Emotional Patterns — 700–1,000 words
   Explore the kinds of beliefs, emotional patterns, and possible internal conflicts (parts conflict) that often accompany this blocker–trait combination. Frame everything as hypotheses to explore in session, not confirmed facts. Include questions the coach can ask to test these hypotheses.

7. Coaching Strategy & Action Plan — 800–1,100 words
   A practical coaching strategy focused on: (a) reducing the influence of the highest blocker, (b) developing the lowest trait, (c) amplifying the highest trait and resource state. Include immediate steps, 30-day focus, and 90-day direction. Suggest coaching exercises, pathways, or programs that may be relevant.

8. Coach Notes — 500–700 words
   Private notes for the practitioner: the main profile story, likely coaching themes, useful questions, strengths to leverage, blind spots to watch for, emotional patterns to approach gently, what not to over-assume, and a suggested first-session structure.

9. Conclusion — 300–500 words
   A warm, professional close that frames the breakthrough opportunity with optimism and realism.

${BREAKTHROUGH_GUARDRAILS}
Write as much as you can within the response. Do not add continuation markers or meta-instructions.`;

export const BREAKTHROUGH_QUICK_TOPICS = [
  {
    label: 'Full Breakthrough Report',
    prompt: BREAKTHROUGH_REPORT_PROMPT,
    isFullReport: true,
  },
  {
    label: 'Primary Blocker',
    prompt: `Analyze the respondent's highest blocker state (the highest score among Antagonism and below on the Harmonic Scale).

Explain:
- What this blocker is and how it may show up in the person's life.
- How it may suppress, distort, or override the person's strongest traits and resource states.
- What shifting it could unlock.
- Specific questions the coach can ask to validate this interpretation.

Frame everything as a coaching hypothesis, not a confirmed fact.

${BREAKTHROUGH_GUARDRAILS}
Length: 1,200–2,000 words.`,
  },
  {
    label: 'Beliefs & Emotions',
    prompt: `Explore the kinds of beliefs, negative emotions, and possible internal conflicts (parts conflict) that often accompany this respondent's blocker–trait combination.

Use the highest blocker and the lowest characteristic as your starting point.

Frame everything as hypotheses to explore in session. Do not present them as confirmed facts.

Include:
- Likely limiting beliefs connected to the blocker.
- Emotional patterns that may reinforce the blocker.
- Possible internal conflicts between the person's higher traits and the blocker state.
- Questions the coach can ask to test each hypothesis.

${BREAKTHROUGH_GUARDRAILS}
Length: 1,200–2,000 words.`,
  },
  {
    label: 'Parts Conflict',
    prompt: `Explore possible internal parts conflict in this respondent.

A "part" is an aspect of the person that has its own positive intention. When two parts have conflicting intentions, the person may feel stuck or self-sabotage.

Use the assessment data to identify:
- Which traits or states may represent conflicting parts.
- What each part may be trying to protect or achieve.
- How the highest blocker may be a protective part that conflicts with the highest trait.
- How the coach might help the client negotiate between these parts.

Frame everything as a hypothesis. Include questions the coach can ask.

${BREAKTHROUGH_GUARDRAILS}
Length: 1,000–1,800 words.`,
  },
  {
    label: 'Unlock Strength',
    prompt: `Identify the respondent's highest characteristic and highest resource state.

Explain how these can be used as a resource for breakthrough:
- How to amplify them.
- How to prevent the highest blocker from draining or distorting them.
- How they can support the development of the lowest trait.
- Practical ways to leverage them in daily life, work, and relationships.

${BREAKTHROUGH_GUARDRAILS}
Length: 1,000–1,800 words.`,
  },
  {
    label: 'Coaching Questions',
    prompt: `Generate a set of powerful coaching questions for this respondent's next session.

Base the questions on:
- The highest blocker and how it may be interfering.
- The lowest characteristic and what it needs.
- The highest characteristic and how it can help.
- The interaction between blocker and traits.
- Possible beliefs, emotions, and parts conflict to explore.

Organise the questions into:
- Opening / rapport questions.
- Exploration questions for the blocker.
- Questions to test belief hypotheses.
- Questions to explore parts conflict.
- Action-oriented questions.

${BREAKTHROUGH_GUARDRAILS}
Length: 800–1,400 words.`,
  },
  {
    label: 'Program Ideas',
    prompt: `Suggest coaching programs, pathways, or structured interventions for this respondent.

Base the suggestions on:
- The highest blocker and the change strategy needed.
- The lowest characteristic and its development needs.
- The highest characteristic as a leverage point.
- The overall breakthrough zone.

Include:
- A suggested 4–6 week coaching focus.
- Specific exercises or techniques that may help shift the blocker.
- Habits or practices that could develop the lowest trait.
- How to use the strongest trait as a resource throughout.
- Possible NLP OS pathways or programs that fit this profile.

${BREAKTHROUGH_GUARDRAILS}
Length: 1,000–1,800 words.`,
  },
  {
    label: 'Action Plan',
    prompt: `Create a practical breakthrough action plan for this respondent.

Include:
- Immediate 7-day steps focused on reducing the highest blocker.
- 30-day development actions for the lowest characteristic.
- 90-day growth direction leveraging the highest trait.
- Reflection questions for the client.
- Suggested coaching focus for the next 3 sessions.
- Practical exercises.

The plan should focus on:
1. Reducing the influence of the highest blocker state.
2. Developing the lowest characteristic.
3. Amplifying the highest trait and resource state.
4. Preventing the blocker from overriding positive traits.

${BREAKTHROUGH_GUARDRAILS}
Length: 1,000–1,800 words.`,
  },
];
